<script setup>
import { computed, inject } from 'vue';
import { ctaButtons, tiqHeroHasContent, tiqHeroPhone } from '../blockContent.js';
import SmartphonePreview from '../SmartphonePreview.vue';
import { accentParts, styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const titleParts = computed(() => accentParts(props.config.title));
// A visitor sees a hero only when it says something, and a phone only
// when it has a screen; the editor keeps the empty frame to fill
// (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const hasContent = computed(() => editing || tiqHeroHasContent(props.config));
const buttons = computed(() => (editing ? (Array.isArray(props.config.buttons) ? props.config.buttons : []) : ctaButtons(props.config)));

const hasPhone = computed(() => (editing ? !!props.config.show_phone : tiqHeroPhone(props.config)));
const phonePosition = computed(() => (props.config.phone_position === 'left' ? 'left' : 'right'));
const phoneWidth = computed(() => Number(props.config.phone_max_width) || 300);

// Padding and min-height overrides, so the same block can be a tall
// marketing hero or a slim banner. Blank falls back to the band's own
// clamp(); the phone-derived default is Classic's (aspect 300:620 plus
// the band's breathing room) so a switched template keeps its height.
const sectionStyle = computed(() => {
    const s = { ...styleVars(props.config) };
    const pt = props.config.padding_top;
    const pb = props.config.padding_bottom;
    const mh = props.config.min_height;
    if (pt !== '' && pt != null) s.paddingTop = `${Number(pt)}px`;
    if (pb !== '' && pb != null) s.paddingBottom = `${Number(pb)}px`;
    if (mh !== '' && mh != null) {
        s.minHeight = Number(mh) === 0 ? '0' : `${Number(mh)}px`;
    } else if (hasPhone.value) {
        s.minHeight = `${Math.round((phoneWidth.value * 620) / 300 + 80)}px`;
    }
    return s;
});
</script>

<template>
    <section v-if="hasContent" :id="config.anchor_id || null"
             class="th-block th-tiqhero th-tint"
             :class="{ 'th-tiqhero--phone': hasPhone, 'th-tiqhero--phone-left': hasPhone && phonePosition === 'left' }"
             :style="sectionStyle">
        <div class="th-wrap th-tiqhero__inner" :class="{ 'th-split': hasPhone }">
            <div class="th-tiqhero__text">
                <span v-if="config.badge" class="th-tiqhero__badge">
                    <span class="th-dot th-tiqhero__badge-dot"></span>{{ config.badge }}
                </span>
                <h1 v-if="titleParts.length || editing" class="th-h1 th-tiqhero__title">
                    <template v-for="(part, i) in titleParts" :key="i">
                        <span v-if="part.accent" class="th-accent-word">{{ part.text }}</span>
                        <template v-else>{{ part.text }}</template>
                    </template>
                </h1>
                <p v-if="config.subtitle" class="th-lede th-tiqhero__lede">{{ config.subtitle }}</p>
                <div v-if="buttons.length" class="th-row th-tiqhero__ctas">
                    <a v-for="(b, i) in buttons" :key="i" :href="b.href || '#'"
                       class="th-btn"
                       :class="(b.style || 'primary') === 'ghost' ? 'th-btn--ghost' : 'th-btn--primary'">
                        {{ b.label }}
                    </a>
                </div>
            </div>

            <div v-if="hasPhone" class="th-tiqhero__visual">
                <SmartphonePreview
                    :media-type="config.phone_media_type || 'image'"
                    :image-url="config.phone_image_url || ''"
                    :video-url="config.phone_video_url || ''"
                    :poster-url="config.phone_poster_url || ''"
                    :bg-color="config.phone_bg_color || 'linear-gradient(160deg,#0f0c29,#302b63,#24243e)'"
                    :frame-style="config.phone_frame_style || 'dark'"
                    :max-width="Number(config.phone_max_width) || 300"
                    :hide-topbar="!!config.phone_hide_topbar"
                />
            </div>
        </div>

        <p v-if="config.scroll_hint" class="th-tiqhero__scroll">
            <span class="th-tiqhero__scroll-line"></span>{{ config.scroll_hint }}
        </p>
    </section>
</template>

<style scoped>
.th-tiqhero { display: flex; flex-direction: column; justify-content: center; overflow: hidden; }
.th-tiqhero__inner {
    width: 100%;
    padding-top: clamp(48px, 7vw, 96px);
    padding-bottom: clamp(48px, 7vw, 96px);
}
.th-tiqhero--phone .th-tiqhero__inner { align-items: center; }
.th-tiqhero--phone-left .th-tiqhero__text { order: 2; }
.th-tiqhero--phone-left .th-tiqhero__visual { order: 1; }
.th-tiqhero__text { min-width: 0; }
.th-tiqhero__visual { display: flex; justify-content: center; }

.th-tiqhero__badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1px solid var(--th-line);
    padding: 7px 12px;
    border-radius: 999px;
    font: 500 12px/1 var(--th-mono);
    letter-spacing: .04em;
    margin-bottom: 22px;
}
.th-tiqhero__badge-dot { width: 8px; height: 8px; background: var(--accent); }
.th-tiqhero__title { font-size: clamp(40px, 6vw, 80px); }
.th-tiqhero__lede { margin-bottom: 28px; }

.th-tiqhero__scroll {
    margin: 0 0 clamp(16px, 2vw, 28px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    font: 500 11px/1 var(--th-mono);
    letter-spacing: .16em;
    text-transform: uppercase;
    color: var(--th-faint);
}
.th-tiqhero__scroll-line { width: 1px; height: 36px; background: currentColor; opacity: .45; }

@media (max-width: 900px) {
    .th-tiqhero--phone-left .th-tiqhero__text,
    .th-tiqhero--phone-left .th-tiqhero__visual { order: initial; }
}
</style>
